import { useCallback, useEffect, useRef, useState } from 'react'

import {
  initKnapWidget,
  type IKnapWidget,
  type IKnapWidgetUser,
} from 'src/app/ask-ai/adapters/knap-widget'
import { useAuthStore } from 'src/app/auth/store/auth-store'

export type KnapWidgetStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface IUseKnapWidgetParams {
  isOpen: boolean
}

export interface IUseKnapWidgetReturn {
  containerRef: React.RefObject<HTMLDivElement>
  status: KnapWidgetStatus
  retry: () => void
}

export const useKnapWidget = ({
  isOpen,
}: IUseKnapWidgetParams): IUseKnapWidgetReturn => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<IKnapWidget | null>(null)
  const widgetTokenRef = useRef<string | null>(null)
  const hasOpenedOnceRef = useRef<boolean>(false)
  const [status, setStatus] = useState<KnapWidgetStatus>('idle')
  const [initAttempt, setInitAttempt] = useState<number>(0)

  const token = useAuthStore(state => state.currentUser?.token)
  const email = useAuthStore(state => state.currentUser?.data.email)
  const name = useAuthStore(state => state.currentUser?.data.name ?? null)
  const restoreStatus = useAuthStore(state => state.restoreStatus)
  const setRestoreStatus = useAuthStore(state => state.setRestoreStatus)

  if (isOpen && !hasOpenedOnceRef.current) {
    hasOpenedOnceRef.current = true
  }

  useEffect((): (() => void) | void => {
    if (!hasOpenedOnceRef.current) return
    if (widgetRef.current) return
    if (!containerRef.current) return

    if (restoreStatus === 'idle' || restoreStatus === 'restoring') {
      setStatus('loading')
      return
    }

    if (restoreStatus === 'retryable-error') {
      setStatus('error')
      return
    }

    if (!token || !email) {
      setStatus('error')
      return
    }

    const user: IKnapWidgetUser = { email, name }
    setStatus('loading')

    let cancelled = false

    initKnapWidget({
      container: containerRef.current,
      accessToken: token,
      user,
      theme: 'light',
    })
      .then((instance): void => {
        if (cancelled) {
          instance.destroy()
          return
        }
        widgetRef.current = instance
        widgetTokenRef.current = token
        setStatus('ready')
      })
      .catch((): void => {
        if (cancelled) return
        setStatus('error')
      })

    return (): void => {
      cancelled = true
    }
  }, [isOpen, token, email, name, restoreStatus, initAttempt])

  const retry = useCallback((): void => {
    if (status !== 'error') return
    setStatus('loading')
    if (restoreStatus === 'retryable-error') {
      setRestoreStatus('idle')
      return
    }
    setInitAttempt(attempt => attempt + 1)
  }, [restoreStatus, setRestoreStatus, status])

  useEffect((): void => {
    if (!token || !widgetRef.current) return
    if (widgetTokenRef.current === token) return
    widgetRef.current.update({ accessToken: token })
    widgetTokenRef.current = token
  }, [token])

  useEffect((): void => {
    if (token) return
    if (!widgetRef.current) return
    widgetRef.current.destroy()
    widgetRef.current = null
    widgetTokenRef.current = null
    setStatus('idle')
    hasOpenedOnceRef.current = false
  }, [token])

  useEffect((): (() => void) => {
    return (): void => {
      if (widgetRef.current) {
        widgetRef.current.destroy()
        widgetRef.current = null
        widgetTokenRef.current = null
      }
    }
  }, [])

  return { containerRef, status, retry }
}
